import { AANAccountType, AccountTypes, IRATrustTypes, YesNoEmpty } from '@interfaces/models';
import { TrustNumber } from '@shared/models/trust';
import {
  aanToTriageAccountTypeMap,
  getAccountTypeFromTriage,
  getImpliedInheritedAccountAnswer,
} from '@src/app/secure/account-triage/helpers/triage-form-mapper.helper';

import {
  HydrationDecedentInfo,
  HydrationEstate,
  HydrationResult,
  HydrationTaxCheckboxSelections,
  HydrationTriage,
  HydrationTrust,
  HydrationTrustTaxInfo,
} from '../hydration.models';
import { booleanToYesNo, firstAddressLine, reverseCountry } from '../hydration.utils';
import type {
  CooDecedentInformation,
  CooDocumentInformation,
  CooEstateInformation,
  CooTrustInformation,
  CooWithholding,
  InheritanceDraft,
} from '../ku4-draft.models';
import { hydrateAccountDetails } from '../mappers/account-details.mapper';
import { buildDocumentFileBuckets } from '../mappers/document-files.mapper';
import { hydratePersons } from '../mappers/person.mapper';
import { hydratePrimaryOwner } from '../mappers/primary-owner.mapper';

const FILETYPE_TO_LABEL: Record<string, string> = {
  INDIVIDUAL_JOINT: 'Individual/joint brokerage',
  IRA: 'IRA',
  ESTATE: 'Estate',
  TRUST_UNDER_AGREEMENT: 'Trust under agreement',
  TRUST_UNDER_WILL: 'Trust under will',
};

const MARITAL_STATUS_TO_YESNO: Record<string, YesNoEmpty> = {
  MARRIED: YesNoEmpty.YES,
  NOT_MARRIED: YesNoEmpty.NO,
};

// Mirrors the trust-type-to-AAN mapping account-triage.component.ts's
// setEligibleTrustAccounts uses forward, so a destination account's real AAN type can be
// mapped back to the triage question's typeOfTrust answer.
const AAN_TO_IRA_TRUST_TYPE: Partial<Record<AANAccountType, IRATrustTypes>> = {
  [AANAccountType.LIVING_TRUST]: IRATrustTypes.UNDER_AGREEMENT_TRUST,
  [AANAccountType.GRANTOR_TRUST]: IRATrustTypes.UNDER_AGREEMENT_TRUST,
  [AANAccountType.UNDER_WILL_TRUST]: IRATrustTypes.UNDER_WILL_TRUST,
};

export function hydrateInheritance(draft: InheritanceDraft): HydrationResult {
  const result: HydrationResult = {};

  if (draft.experienceType !== undefined) {
    result.experienceType = draft.experienceType;
  }

  const triage = buildTriage(draft);

  if (triage) {
    result.triage = triage;
  }

  const decedentInfo = buildDecedentInfo(draft.decedentInformation);

  if (decedentInfo) {
    result.decedentInfo = decedentInfo;
  }

  const trust = buildTrust(draft.trustInformation);

  if (trust) {
    result.trust = trust;
  }

  const estate = buildEstate(draft.estateInformation) ?? {};

  if (!trust) {
    applyWithholding(draft.withholding, estate);
  }

  if (Object.keys(estate).length) {
    result.estate = estate;
  }

  const trustTaxInfo = buildTrustTaxInfo(draft.trustInformation, draft.withholding);

  if (trustTaxInfo) {
    result.trustTaxInfo = trustTaxInfo;
  }

  const beneficiaries = hydratePersons(draft.beneficiaries);

  if (beneficiaries) {
    result.beneficiaries = beneficiaries;
  }

  const accountDetails = hydrateAccountDetails(draft.accountDetails);

  if (accountDetails.selectedAccs) {
    result.selectedAccs = accountDetails.selectedAccs;
  }

  if (accountDetails.positions) {
    result.positions = accountDetails.positions;
  }

  if (accountDetails.settlementFund) {
    result.settlementFund = accountDetails.settlementFund;
  }

  if (accountDetails.investmentObjectives) {
    result.investmentObjectives = accountDetails.investmentObjectives;
  }

  const primaryOwner = hydratePrimaryOwner(draft.primaryOwner);

  if (primaryOwner) {
    result.primaryOwner = primaryOwner;
  }

  const documentFileBuckets = buildDocumentFileBuckets(draft.documentInformation?.uploadedFiles);
  Object.assign(result, documentFileBuckets);

  const declaredAccountTypes = buildDeclaredAccountTypes(
    draft.documentInformation?.declaredFileTypes,
  );

  if (declaredAccountTypes) {
    result.declaredAccountTypes = declaredAccountTypes;
  }

  const taxCheckboxSelections = buildTaxCheckboxSelections(
    draft.documentInformation?.taxWaiverStatus,
  );

  if (taxCheckboxSelections) {
    result.taxCheckboxSelections = taxCheckboxSelections;
  }

  if (draft.documentInformation?.vanguardHasLetter !== undefined) {
    result.vanguardHasLetter = draft.documentInformation.vanguardHasLetter;
  }

  return result;
}

function buildDeclaredAccountTypes(declaredFileTypes: string[] | undefined): string[] | undefined {
  const labels = (declaredFileTypes ?? [])
    .map((code) => FILETYPE_TO_LABEL[code])
    .filter((label): label is string => !!label);

  return labels.length ? labels : undefined;
}

// Expands the draft's single taxWaiverStatus value back into the L8/ITW
// page's 3 mutually exclusive checkboxes.
function buildTaxCheckboxSelections(
  taxWaiverStatus: CooDocumentInformation['taxWaiverStatus'],
): HydrationTaxCheckboxSelections | undefined {
  switch (taxWaiverStatus) {
    case 'MAILING':
      return { mailingForm: true };
    case 'ALREADY_SUBMITTED':
      return { beneficiarySubmitted: true };
    case 'NOT_NEEDED':
      return { noFormNeeded: true };
    default:
      return undefined;
  }
}

function buildTriage(draft: InheritanceDraft): HydrationTriage | undefined {
  const result: HydrationTriage = {};

  const isSpouse = booleanToYesNo(draft.spouse);

  if (isSpouse !== undefined) {
    result.isSpouse = isSpouse;
  }

  const isSingleExecutor = booleanToYesNo(draft.executor);

  if (isSingleExecutor !== undefined) {
    result.isSingleExecutor = isSingleExecutor;
  }

  if (draft.individualDesignation !== undefined) {
    result.individualDesignation = draft.individualDesignation;
  }

  if (draft.iraDesignation !== undefined) {
    result.IRADesignation = draft.iraDesignation;
  }

  if (draft.iraOption !== undefined) {
    result.iraOption = draft.iraOption;
  }

  const accountDetails = draft.accountDetails?.[0];

  // Prefills the original decedent on Account Triage when the account was previously inherited.
  // The whole typed name is stored in firstName, not split across name parts: KU4 declares no
  // flat field for it, so the only place it can live is this structured originalDecedent, and
  // splitting a name the user typed would not round-trip. wasAccountPreviouslyInherited below is
  // inferred from the same source account, so the two answers always come from one place.
  const originalDecedentName = accountDetails?.sourceAccount?.originalDecedent?.firstName;

  if (originalDecedentName) {
    result.originalDecedentName = originalDecedentName;
  }

  const sourceType = (accountDetails?.sourceAccount?.accountType ??
    accountDetails?.destinationAccounts?.[0]?.accountType) as AANAccountType | undefined;

  if (sourceType) {
    const inherited = getImpliedInheritedAccountAnswer(new Set([sourceType]));

    if (inherited !== YesNoEmpty.EMPTY) {
      result.wasAccountPreviouslyInherited = inherited;
    }

    const triageAccountType = aanToTriageAccountTypeMap[sourceType]?.[0];

    if (triageAccountType) {
      const { accountType, iraType } = getAccountTypeFromTriage(triageAccountType, inherited);

      if (accountType !== AccountTypes.EMPTY) {
        result.accountTypeInheriting = accountType;
      }

      if (iraType) {
        result.iraType = iraType;
      }
    }
  }

  if (draft.iraDesignation === 'EXECUTOR' || draft.individualDesignation === 'EXECUTOR') {
    result.isOnlyExecutor = YesNoEmpty.YES;
  }

  if (draft.iraDesignation === 'TRUSTEE' || draft.individualDesignation === 'TRUSTEE') {
    result.isOnlyTrustee = YesNoEmpty.YES;
  }

  // Executors/trustees receive into an Estate/Trust account, which aanToTriageAccountTypeMap
  // has no entry for, so accountTypeInheriting above stays unresolved. JOINT already resolves
  // it via its own sourceAccount, so an absent sourceAccount here most likely means INDV.
  if (
    result.accountTypeInheriting === undefined &&
    (draft.individualDesignation === 'EXECUTOR' || draft.individualDesignation === 'TRUSTEE') &&
    !accountDetails?.sourceAccount?.accountType
  ) {
    result.accountTypeInheriting = AccountTypes.INDV;
  }

  if (draft.onlyBeneficiary !== undefined) {
    result.isOnlyBeneficiary = draft.onlyBeneficiary ? YesNoEmpty.YES : YesNoEmpty.NO;
  }

  // The "only child/grandchild" question appears only in the descendant flow
  // (IRA + per-stirpes relationship = iraDesignation 'RELATIONSHIP'). To continue
  // past that page the user must either add beneficiaries OR answer only-child =
  // YES, and the draft is saved on Continue — so within the descendant flow, no
  // beneficiaries implies only-child = YES; beneficiaries present implies NO.
  if (draft.iraDesignation === 'RELATIONSHIP') {
    result.isOnlyChild = (draft.beneficiaries?.length ?? 0) === 0;
  } else if ((draft.beneficiaries?.length ?? 0) > 0) {
    result.isOnlyChild = false;
  }

  applyTrustToTrustDerivations(draft, result);
  applyTrustInformationDerivations(draft.trustInformation, result);
  applyMultiInheritanceDerivations(draft, result);
  applyTrusteeOrEstateDerivation(draft, result);
  applyMaritalStatusDerivation(draft.decedentInformation, result);

  // Recover typeOfTrust from the selected destination account's real AAN type (mirrors
  // account-triage.component.ts's setEligibleTrustAccounts). Only a fallback: no trust-type
  // destination account means nothing to recover.
  if (result.typeOfTrust === undefined) {
    const destinationAccountType = accountDetails?.destinationAccounts?.[0]?.accountType as
      | AANAccountType
      | undefined;
    const typeOfTrust = AAN_TO_IRA_TRUST_TYPE[destinationAccountType as AANAccountType];

    if (typeOfTrust) {
      result.typeOfTrust = typeOfTrust;
    }
  }

  if (Object.keys(result).length === 0) {
    return undefined;
  }

  result.flowType =
    draft.experienceType === 'MULTI_INHERITANCE' ? 'MULTI_INHERITANCE' : 'INHERITANCE';

  return result;
}

function applyTrustToTrustDerivations(draft: InheritanceDraft, result: HydrationTriage): void {
  if (draft.experienceType === 'TRUST') {
    result.isUpdatingTrust = true;
    result.hasPassedAway = YesNoEmpty.YES;
    result.isSuccessorOrLivingTrustee = YesNoEmpty.YES;
    result.accountTypeInheriting = AccountTypes.TRUST;
  }
}

function applyTrustInformationDerivations(
  trust: CooTrustInformation | undefined,
  result: HydrationTriage,
): void {
  if (!trust) {
    return;
  }

  if (trust.trusteeOrg !== undefined) {
    result.isTrusteeOrg = trust.trusteeOrg ? YesNoEmpty.YES : YesNoEmpty.NO;
  }

  if (trust.singleTrustee !== undefined) {
    result.isSingleTrustee = trust.singleTrustee ? YesNoEmpty.YES : YesNoEmpty.NO;
  }

  if (trust.typeOfTrust !== undefined) {
    result.typeOfTrust = trust.typeOfTrust;
  }
}

function applyMultiInheritanceDerivations(draft: InheritanceDraft, result: HydrationTriage): void {
  if (draft.experienceType === 'MULTI_INHERITANCE') {
    result.isInheritingMultipleAccounts = YesNoEmpty.YES;
  }
}

// Best-effort derivation: cannot distinguish an unanswered question from a NO
// answer, so we only set YES when the KU4 draft carries trust/estate data.
function applyTrusteeOrEstateDerivation(draft: InheritanceDraft, result: HydrationTriage): void {
  if (draft.trustInformation || draft.estateInformation) {
    result.isTrusteeOrEstate = YesNoEmpty.YES;
  }
}

function applyMaritalStatusDerivation(
  decedent: CooDecedentInformation | undefined,
  result: HydrationTriage,
): void {
  const status = decedent?.maritalStatus;

  if (status === undefined) {
    return;
  }

  const value = MARITAL_STATUS_TO_YESNO[status];

  if (value !== undefined) {
    result.isDecedentMarried = value;
  }
}

function buildDecedentInfo(
  decedent: CooDecedentInformation | undefined,
): HydrationDecedentInfo | undefined {
  if (!decedent) {
    return undefined;
  }

  const result: HydrationDecedentInfo = {};

  if (decedent.firstName !== undefined) {
    result.firstName = decedent.firstName;
  }

  if (decedent.middleName !== undefined) {
    result.middleName = decedent.middleName;
  }

  if (decedent.lastName !== undefined) {
    result.lastName = decedent.lastName;
  }

  if (decedent.tin !== undefined) {
    result.decedentId_private = decedent.tin;
  }

  if (decedent.zip !== undefined) {
    result.zipCode = decedent.zip;
  }

  if (decedent.primaryResidence) {
    const addressLine1 = firstAddressLine(decedent.primaryResidence);

    if (addressLine1 !== undefined) {
      result.addressLine1 = addressLine1;
    }

    if (decedent.primaryResidence.city !== undefined) {
      result.city = decedent.primaryResidence.city;
    }

    if (decedent.primaryResidence.state !== undefined) {
      result.state = decedent.primaryResidence.state;
    }

    if (decedent.primaryResidence.zip !== undefined) {
      result.zipCode = decedent.primaryResidence.zip;
    }
  }

  if (decedent.birthdate !== undefined) {
    result.dateOfBirth = decedent.birthdate;
  }

  if (decedent.dateOfDeath !== undefined) {
    result.dateOfDeath = decedent.dateOfDeath;
  }

  return Object.keys(result).length ? result : undefined;
}

function buildTrust(trust: CooTrustInformation | undefined): HydrationTrust | undefined {
  if (!trust?.trustName) {
    return undefined;
  }

  const result: HydrationTrust = { trustName: trust.trustName };

  if (trust.establishedDate !== undefined) {
    result.trustDate = trust.establishedDate;
  }

  const countryQuestion = reverseCountry(trust.establishedCountry);

  if (countryQuestion !== undefined) {
    result.countryQuestion = countryQuestion;
  }

  const taxResidencyQuestion = reverseCountry(trust.taxResidency);

  if (taxResidencyQuestion !== undefined) {
    result.taxResidencyQuestion = taxResidencyQuestion;
  }

  if (trust.typeOfTrust !== undefined) {
    result.typeOfTrust = trust.typeOfTrust;
  }

  const numberQuestion = booleanToYesNo(trust.singleTrustee);

  if (numberQuestion !== undefined) {
    result.numberQuestion = numberQuestion;
  }

  // grantorQuestion (grantor's country of citizenship) lives on the trust slice
  // for the multi-inheritance flow only — see trust-tax-info.component's
  // updateState(), which splits the form value into `trust.grantorQuestion` +
  // `trustTaxInfo` for multi, vs. a flat TrustTaxUpdate for single/trust-to-trust.
  const grantorQuestion = reverseCountry(trust.grantor?.country);

  if (grantorQuestion !== undefined) {
    result.grantorQuestion = grantorQuestion;
  }

  // trustAddress is the "Same as trustee address" checkbox: true = same (no
  // separate address entered), false = a separate primaryResidence was entered.
  result.trustAddress = trust.primaryResidenceSameAsTrustee ?? !trust.primaryResidence;

  if (trust.primaryResidence) {
    const address = firstAddressLine(trust.primaryResidence);

    if (address !== undefined) {
      result.address = address;
    }

    if (trust.primaryResidence.city !== undefined) {
      result.city = trust.primaryResidence.city;
    }

    if (trust.primaryResidence.state !== undefined) {
      result.state = trust.primaryResidence.state;
    }

    if (trust.primaryResidence.zip !== undefined) {
      result.zipCode = trust.primaryResidence.zip;
    }
  }

  // mailingAddress is the "Same as trust address" checkbox: true = same,
  // false = a separate primaryMailing was entered.
  result.mailingAddress = trust.primaryMailingSameAsTrust ?? !trust.primaryMailing;

  if (trust.primaryMailing) {
    const mailAddress = firstAddressLine(trust.primaryMailing);

    if (mailAddress !== undefined) {
      result.mailAddress = mailAddress;
    }

    if (trust.primaryMailing.city !== undefined) {
      result.mailCity = trust.primaryMailing.city;
    }

    if (trust.primaryMailing.state !== undefined) {
      result.mailState = trust.primaryMailing.state;
    }

    if (trust.primaryMailing.zip !== undefined) {
      result.mailZipCode = trust.primaryMailing.zip;
    }
  }

  return result;
}

function buildEstate(estate: CooEstateInformation | undefined): HydrationEstate | undefined {
  if (!estate) {
    return undefined;
  }

  const result: HydrationEstate = {};

  if (estate.tin !== undefined) {
    result.tin_private = estate.tin;
  }

  if (estate.name !== undefined) {
    result.estateName = estate.name;
  }

  const countryQuestion = reverseCountry(estate.establishedCountry);

  if (countryQuestion !== undefined) {
    result.countryQuestion = countryQuestion;
  }

  const taxResidencyQuestion = reverseCountry(estate.taxResidency);

  if (taxResidencyQuestion !== undefined) {
    result.taxResidencyQuestion = taxResidencyQuestion;
  }

  if (estate.executorSignature !== undefined) {
    result.signature = estate.executorSignature;
  }

  return Object.keys(result).length ? result : undefined;
}

// Restores the trust-tax-info page state (EIN/SSN + grantor name). The
// draft's `tin` is a single flattened field with no explicit EIN-vs-SSN flag,
// so we infer the path from `grantor` presence: the grantor name/country is
// only ever collected on the SSN branch of the trust-tax-info form, so a
// present `grantor` means the SSN path was taken and `tin` is the SSN;
// otherwise `tin` is the EIN.
function buildTrustTaxInfo(
  trust: CooTrustInformation | undefined,
  withholding: CooWithholding | undefined,
): HydrationTrustTaxInfo | undefined {
  if (!trust?.trustName) {
    return undefined;
  }

  const result: HydrationTrustTaxInfo = {};
  const isSSNPath = !!trust.grantor;

  if (trust.tin !== undefined) {
    if (isSSNPath) {
      result.ssn = trust.tin;
    } else {
      result.ein = trust.tin;
    }

    result.numberQuestion = isSSNPath ? TrustNumber.SSN : TrustNumber.EIN;
  }

  if (trust.grantor?.name !== undefined) {
    result.grantorName = trust.grantor.name;
  }

  if (withholding?.subjectToBackupWithholding !== undefined) {
    result.withholdingQuestion = withholding.subjectToBackupWithholding
      ? YesNoEmpty.YES
      : YesNoEmpty.NO;
  }

  if (withholding?.certificationDate !== undefined) {
    result.date = withholding.certificationDate;
  }

  return Object.keys(result).length ? result : undefined;
}

// withholding lives in its own draft-level object, separate from
// EstateInformation, so trust-to-trust drafts (which have no
// estateInformation) can still carry it. The estate withholding radio uses
// mixed-case 'Yes'/'No' values, unlike the legacy triage radios (isSpouse /
// isSingleExecutor) which use uppercase. booleanToYesNo() is shared with
// those legacy callers and MUST stay uppercase, so map to the mixed-case
// YesNoEmpty enum locally.
function applyWithholding(withholding: CooWithholding | undefined, result: HydrationEstate): void {
  if (!withholding) {
    return;
  }

  if (withholding.subjectToBackupWithholding !== undefined) {
    result.withholdingQuestion = withholding.subjectToBackupWithholding
      ? YesNoEmpty.YES
      : YesNoEmpty.NO;
  }

  if (withholding.certificationDate !== undefined) {
    result.date = withholding.certificationDate;
  }
}
